import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import RenderHTML, { defaultSystemFonts } from 'react-native-render-html';

import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { ACHIEVEMENTS_FONT as FONT, PANEL_WIDTH } from '../Achievements/achievementsTheme';
import { RedesignScrollView } from '../common/RedesignScrollView';
import RedesignModal from './RedesignModal';
import {
	createPopupHtmlStyles,
	POPUP,
	POPUP_TINT,
	popupBodyStyle,
	popupColors,
	PopupDivider,
	PopupHeader,
} from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

/**
 * Game Rules, opened from the Settings popup's "Game rules" row. Drawn as a sibling of
 * "Modal / Settings" (5054:78827): the same panel and header over a scrolling body that sets the
 * server's rules HTML in the popups' type ramp - gold uppercase section heads over secondary copy.
 * The body scrolls, so on a short screen the panel simply gets shorter.
 */
const PANEL_HEIGHT = 640;
/** Room the scroll body keeps on its right for the 4-wide scroll bar. */
const SCROLL_GUTTER = 14;

interface GameRulesModalProps {
	onClose: () => void;
}

function GameRulesModal({ onClose }: GameRulesModalProps) {
	const { t } = useTranslation();
	const scale = useRedesignScale();
	const styles = useMemo(() => createStyles(scale), [scale]);
	const tagsStyles = useMemo(() => createPopupHtmlStyles(scale), [scale]);
	const systemFonts = useMemo(() => [...defaultSystemFonts, FONT], []);

	useEffect(() => {
		analyticsEvents.trackOpenRules();
	}, []);

	const html = t('T_SITE_GAME_RULES_CONTENT');
	const htmlWidth = (PANEL_WIDTH - 2 * POPUP.paddingX - SCROLL_GUTTER) * scale;

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{ height: PANEL_HEIGHT, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View style={styles.body}>
				<PopupHeader
					scale={scale}
					title={t('T_SITE_GAME_RULES_TITLE')}
					onClose={onClose}
					closeLabel={t('T_SITE_A11Y_CLOSE_MODAL', { defaultValue: 'Close modal' })}
				/>
				<PopupDivider scale={scale} />
				<RedesignScrollView
					contentContainerStyle={styles.content}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					<RenderHTML
						contentWidth={htmlWidth}
						source={{ html }}
						systemFonts={systemFonts}
						tagsStyles={tagsStyles}
						baseStyle={styles.htmlBase}
					/>
				</RedesignScrollView>
			</View>
		</RedesignModal>
	);
}

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: popupBodyStyle(scale),
		content: {
			paddingRight: s(SCROLL_GUTTER),
			paddingBottom: s(4),
		},
		htmlBase: {
			fontFamily: FONT,
			color: popupColors.secondary,
			fontSize: s(14),
			lineHeight: s(20),
		},
	});
};

export default GameRulesModal;
